import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Minus, Trash2, Tag, ArrowRight, ShoppingBag, Lock, Truck, FlaskConical, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/contexts/CartContext";
import { trackCart } from "@/lib/analytics";
import Thumb from "@/components/visual/Thumb";
import Offers from "@/components/shop/Offers";
import AddToBasket from "@/components/shop/AddToBasket";
import { useCatalog } from "@/lib/shop";

/** Smallest in stock jar that closes the free delivery gap. */
function TopUp({ gap, items }) {
  const products = useCatalog();
  if (!products || gap <= 0) return null;
  const inCart = new Set(items.map((i) => i.variant_id));
  // Cheapest single jar that closes the gap, including one more of a size
  // already in the basket. Skip it if it costs far more than the delivery fee saves.
  const options = products
    .filter((p) => (p.kind || "bottle") === "bottle")
    .flatMap((p) => (p.variants || []).map((v) => ({ p, v })))
    .filter(({ v }) => v.stock > 0 && v.price >= gap && v.price - gap <= 300)
    .sort((a, b) => a.v.price - b.v.price);
  const pick = options[0];
  if (!pick) return null;
  const again = inCart.has(pick.v.id);
  return (
    <div className="rounded-2xl bg-brand-parchment-soft p-3 flex items-center gap-3" data-testid="cart-topup">
      <div className="flex-1 min-w-0">
        <p className="font-body text-[12px] font-semibold text-brand-gold">Unlock free delivery</p>
        <p className="font-body text-[14px] text-brand-ink leading-snug">{again ? "One more " : ""}{pick.p.name.replace("Karijeeva ", "")}, {pick.v.size} · ₹{pick.v.price}</p>
      </div>
      <AddToBasket product={pick.p} variant={pick.v} label="Add" className="h-9 px-4 text-[13px]" testid="cart-topup-add" />
    </div>
  );
}

export default function CartDrawer() {
  const {
    items, totals, coupon, welcomeCode,
    drawerOpen, setDrawerOpen,
    setQty, removeItem, applyCoupon, removeCoupon,
  } = useCart();
  const [codeInput, setCodeInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (drawerOpen && items.length) trackCart("view_cart", items);
    // Only when the drawer opens, not on every quantity change inside it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  const handleApply = async (e, code = codeInput) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setApplying(true);
    await applyCoupon(code.trim());
    setApplying(false);
  };

  const freeShipPct = Math.min(100, Math.round(((totals.freeShippingAt - totals.toFreeShipping) / totals.freeShippingAt) * 100));

  const handleCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        className="bg-brand-parchment border-black/10 w-full sm:max-w-md flex flex-col p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-black/10 text-left">
          <SheetTitle className="font-display text-[26px] tracking-heading text-brand-ink">
            Your basket
          </SheetTitle>
          <SheetDescription className="font-body text-sm text-ink-muted">
            {totals.count} item{totals.count === 1 ? "" : "s"}
          </SheetDescription>
          {items.length > 0 && (
            <div className="pt-3" data-testid="cart-free-shipping">
              <p className="font-body text-[13px] text-brand-ink flex items-center gap-2">
                <Truck className="w-4 h-4 text-brand-gold shrink-0" />
                {totals.toFreeShipping > 0
                  ? <>Add <strong>₹{Math.ceil(totals.toFreeShipping)}</strong> more for free delivery.</>
                  : <>Free delivery on this order.</>}
              </p>
              <div className="mt-2 h-1 rounded-full bg-black/10 overflow-hidden" aria-hidden="true">
                <div className="h-full bg-brand-gold transition-all duration-500" style={{ width: `${freeShipPct}%` }} />
              </div>
              <div className="mt-3"><TopUp gap={totals.toFreeShipping} items={items} /></div>
            </div>
          )}
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="w-20 h-20 rounded-full bg-brand-parchment-soft flex items-center justify-center">
              <ShoppingBag className="w-8 h-8 text-brand-obsidian" />
            </div>
            <h3 className="font-display text-h4 text-brand-obsidian mt-6">Your basket is empty.</h3>
            <p className="font-body text-sm text-ink-muted mt-2">
              One ingredient, pressed cold in small batches. Trying it for the first time? Start with 250 ml.
            </p>
            <Button asChild variant="primary" size="md" className="mt-6" onClick={() => setDrawerOpen(false)}>
              <Link to="/products">Shop the oils <ArrowRight /></Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="cart-items">
              {items.map((it) => (
                <div key={it.variant_id} className="flex gap-4 pb-4 border-b border-black/10 last:border-b-0" data-testid={`cart-item-${it.variant_id}`}>
                  <Link to={`/products/${it.slug}`} onClick={() => setDrawerOpen(false)} className="w-20 h-20 rounded-md overflow-hidden bg-brand-parchment-soft shrink-0">
                    <Thumb src={it.image} alt={it.product_name} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${it.slug}`} onClick={() => setDrawerOpen(false)} className="block font-display text-lg text-brand-obsidian leading-tight hover:text-brand-gold">
                      {it.product_name}
                    </Link>
                    <p className="font-body text-xs text-ink-muted mt-1">{it.variant_size}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center h-9 border border-brand-gold/30 rounded-pill bg-white">
                        <button aria-label="Decrease" className="w-9 h-full text-brand-obsidian hover:text-brand-gold" onClick={() => setQty(it.variant_id, it.quantity - 1)}><Minus className="w-3 h-3 mx-auto" /></button>
                        <span className="w-8 text-center font-display text-sm text-brand-obsidian" data-testid={`cart-qty-${it.variant_id}`}>{it.quantity}</span>
                        <button aria-label="Increase" className="w-9 h-full text-brand-obsidian hover:text-brand-gold" onClick={() => setQty(it.variant_id, it.quantity + 1)}><Plus className="w-3 h-3 mx-auto" /></button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-display text-lg text-brand-obsidian">₹{it.unit_price * it.quantity}</span>
                        <button aria-label="Remove" onClick={() => removeItem(it.variant_id)} className="text-brand-husk/50 hover:text-brand-gold" data-testid={`cart-remove-${it.variant_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Coupon + totals */}
            <div className="px-6 py-4 border-t border-black/10 bg-brand-parchment-soft">
              {coupon ? (
                <div className="flex items-center justify-between bg-white rounded-md px-4 py-3" data-testid="cart-coupon-applied">
                  <div>
                    <p className="font-body text-sm font-semibold text-brand-ink">{coupon.code} applied</p>
                    <p className="font-body text-xs text-ink-muted">You save ₹{coupon.final_discount_amount}</p>
                  </div>
                  <button onClick={removeCoupon} className="text-ink-muted hover:text-brand-ink font-body text-xs underline">
                    Remove
                  </button>
                </div>
              ) : welcomeCode?.code ? (
                <div className="flex items-center justify-between gap-3 bg-white rounded-md px-4 py-3" data-testid="cart-welcome-banner">
                  <p className="font-body text-sm text-brand-ink">Your welcome code <strong>{welcomeCode.code}</strong> is ready.</p>
                  <Button variant="secondary" size="sm" loading={applying} onClick={(e) => handleApply(e, welcomeCode.code)} data-testid="cart-welcome-apply">
                    Apply
                  </Button>
                </div>
              ) : !showCode ? (
                <div className="space-y-3">
                  <Offers compact onApply={(code) => handleApply(null, code)} />
                  <button onClick={() => setShowCode(true)} className="font-body text-[13px] text-ink-muted hover:text-brand-ink underline" data-testid="cart-coupon-toggle">
                    Have a different code?
                  </button>
                </div>
              ) : (
                <form onSubmit={handleApply} className="flex gap-2" data-testid="cart-coupon-form">
                  <div className="relative flex-1">
                    <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold" />
                    <Input
                      autoFocus
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                      placeholder="Code"
                      aria-label="Discount code"
                      data-testid="cart-coupon-input"
                      className="h-11 pl-10 bg-white border-black/15 focus-visible:ring-brand-gold"
                    />
                  </div>
                  <Button type="submit" variant="secondary" size="md" loading={applying} disabled={!codeInput}>
                    Apply
                  </Button>
                </form>
              )}

              <dl className="mt-5 space-y-2 font-body text-sm">
                <Row label="Subtotal" value={`₹${totals.subtotal.toFixed(2)}`} />
                {totals.discount > 0 && <Row label="Discount" value={`− ₹${totals.discount.toFixed(2)}`} className="text-brand-gold" />}
                <Row label="Delivery" value={totals.shipping === 0 ? "Free" : `₹${totals.shipping}`} />
                <div className="h-px bg-black/10 my-2" />
                <Row label={<span className="font-display text-h4 text-brand-obsidian">Total</span>} value={<span className="font-display text-h4 text-brand-obsidian" data-testid="cart-total">₹{totals.total.toFixed(2)}</span>} big />
                <p className="text-right font-body text-xs text-ink-muted">Inclusive of GST</p>
              </dl>

              <Button
                data-testid="cart-checkout-btn"
                variant="primary" size="lg"
                className="w-full mt-5"
                onClick={handleCheckout}
              >
                <Lock className="w-4 h-4" /> Checkout securely
              </Button>
              <p className="mt-2 text-center font-body text-xs text-ink-muted">UPI, cards and netbanking through Razorpay</p>
              <ul className="mt-4 grid grid-cols-2 gap-2 font-body text-[12px] text-ink-muted">
                <li className="flex items-center gap-1.5"><FlaskConical className="w-3.5 h-3.5 text-brand-gold" /> Every batch lab tested</li>
                <li className="flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5 text-brand-gold" /> 7 day returns, unopened</li>
              </ul>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value, className="", big }) {
  return (
    <div className={`flex items-center justify-between ${big ? "" : ""} ${className}`}>
      <dt className={big ? "" : "text-ink-muted"}>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
